import React__default from 'react';
import styled from 'styled-components';
import { getColor } from '../styles/stylePropTypes.js';
import { HD_ICONS } from './icons.js';

const ICON_SIZE_REM = {
    xsmall: 1.25,
    small: 1.5,
    medium: 2,
    large: 2.5,
    xlarge: 3
};
const getSizeRem = (size)=>{
    let sizeRem = ICON_SIZE_REM.medium;
    if (size !== undefined) {
        // For historical API reasons, the specific size passed is increased by 0.25.
        sizeRem = typeof size === "number" ? size + 0.25 : ICON_SIZE_REM[size];
    }
    return sizeRem;
};
const StyledSpan = styled.span`
  height: 1em;
  width: 1em;
  display: inline-block;
  height: ${(props)=>getSizeRem(props.$size)}rem;
  width: ${(props)=>getSizeRem(props.$size)}rem;
  font-size: ${(props)=>getSizeRem(props.$size)}rem;
  line-height: ${(props)=>getSizeRem(props.$size)}rem;
  color: ${(props)=>props.$color ? getColor(props.$color, props.theme) : "inherit"};

  &::before {
    line-height: inherit;
  }

  & > svg {
    height: 1em;
    width: 1em;
    overflow: hidden;
    fill: currentColor;
  }
`;
/**
 * Creates a custom Icon component, along with required Icon Spritesheet.
 *
 * Use this to generate a set of Custom icons that can be used throughout an app. These custom Icons can also be used
 * within other 'usual' icon props by being passed as a component to the prop.
 *
 * @example
 * const {
 *    IconSpriteSheet: CustomIconSpriteSheet,
 *    Icon: CustomIcon
 * } = makeCustomIconSpritesheet(CUSTOM_ICONS)
 *
 * <ButtonIcon icon={<CustomIcon icon="pizza" />} />
 */ const makeCustomIconSpritesheet = (icons)=>{
    const sprites = [];
    for(const key in icons){
        const element = icons[key];
        sprites.push(React__default.cloneElement(element, {
            key: `icon-${key}`,
            id: `icon-${key}`
        }));
    }
    const IconSpriteSheet = ()=>{
        return React__default.createElement("svg", {
            "data-hd-iconspritesheet": true,
            xmlns: "http://www.w3.org/2000/svg",
            style: {
                height: "0px",
                width: "0px",
                position: "absolute"
            }
        }, sprites);
    };
    const Icon = ({ icon , color , size ="medium" , ...props })=>{
        let iconCode;
        if (typeof icon !== "string" && React__default.isValidElement(icon)) {
            const el = React__default.Children.only(icon);
            // Pull the icon code and size from the passed element
            iconCode = el.props.icon;
            size = el.props.size ?? size;
        } else {
            iconCode = icon;
        }
        return React__default.createElement(StyledSpan, {
            "data-hd-icon": true,
            ...props,
            "$color": color,
            "$size": size
        }, React__default.createElement("svg", {
            className: "icon",
            "aria-hidden": "true",
            focusable: "false"
        }, React__default.createElement("use", {
            href: `#icon-${iconCode}`
        })));
    };
    Icon.dataId = "data-hd-icon";
    Icon.displayName = "Icon";
    return {
        /**
         * SVG Sprite sheet of Icons.
         *
         * Required to display Icons with the Icon component.
         */ IconSpriteSheet,
        /**
         * Icon - Show an icon from the standard font icon set
         *
         * @example
         * <Icon icon="add"  />
         */ Icon: Icon
    };
};
const { IconSpriteSheet , Icon  } = makeCustomIconSpritesheet(HD_ICONS);
/**
 * Design System Internal usage Icon, exposes a slightly different prop type to the external one.
 *
 * This allows React Elements to be passed as an icon prop, but not allow that externally to the package.
 */ const __Icon = Icon;

export { Icon, IconSpriteSheet, __Icon, makeCustomIconSpritesheet };
